import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import AxiosInstance from "../../services/networkServices";
import { listDance } from "../../http/dance";
import { listKaraoke } from "../../http/karaoke";

export const MAX_THEME_ASSIGNMENTS = 20;

const toOption = (item, fallbackTitle) => ({
  _id: String(item._id),
  title: item.title || fallbackTitle,
  artist: item.artist || "",
});

async function searchDances(search) {
  const response = await listDance({
    page: 1,
    limit: 50,
    searchBy: search || undefined,
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  return (response?.data?.dances || []).map((dance) =>
    toOption(dance, "Untitled dance")
  );
}

async function searchKaraoke(search) {
  const response = await listKaraoke({
    page: 1,
    limit: 50,
    searchBy: search || undefined,
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  return (response?.data?.karaokes || []).map((song) =>
    toOption(song, "Untitled karaoke")
  );
}

async function searchChallenges(search) {
  const response = await AxiosInstance.post("/category/challenge/list", {
    page: 1,
    limit: 50,
    ...(search ? { search } : {}),
    sortBy: "createdAt",
    sortOrder: -1,
  });
  return (response?.data?.data?.list || []).map((challenge) =>
    toOption(challenge, "Untitled challenge")
  );
}

const FIELDS = [
  {
    key: "dances",
    label: "Dances",
    placeholder: "Search dances",
    search: searchDances,
    optionLabel: (option) => option.title,
  },
  {
    key: "karaokes",
    label: "Karaoke",
    placeholder: "Search karaoke",
    search: searchKaraoke,
    optionLabel: (option) =>
      option.artist ? `${option.title} — ${option.artist}` : option.title,
  },
  {
    key: "challenges",
    label: "Challenges",
    placeholder: "Search challenges",
    search: searchChallenges,
    optionLabel: (option) => option.title,
  },
];

function AssignmentSelect({
  label,
  placeholder,
  value,
  onChange,
  search,
  optionLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const next = await search(query.trim());
        if (!cancelled) setOptions(next);
      } catch (error) {
        console.error(error);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query, search]);

  const mergedOptions = [
    ...value,
    ...options.filter(
      (option) => !value.some((selected) => selected._id === option._id)
    ),
  ];

  return (
    <Autocomplete
      multiple
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={mergedOptions}
      value={value}
      loading={loading}
      filterOptions={(opts) => opts}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, selected) => option._id === selected._id}
      onInputChange={(_event, next, reason) => {
        if (reason === "input") setQuery(next);
        if (reason === "clear") setQuery("");
      }}
      onChange={(_event, next) => onChange(next.slice(0, MAX_THEME_ASSIGNMENTS))}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
    />
  );
}

export function assignedFromTheme(theme, docsKey, idsKey, fallbackTitle) {
  const docs = theme?.[docsKey] || [];
  const byId = new Map(docs.map((doc) => [String(doc._id), doc]));
  const ids = (theme?.[idsKey] || []).map((id) => String(id));
  const ordered = ids.length ? ids : docs.map((doc) => String(doc._id));
  return ordered
    .map((id) => {
      const doc = byId.get(id);
      if (!doc && !id) return null;
      return toOption(doc || { _id: id }, fallbackTitle);
    })
    .filter(Boolean);
}

export default function AssignedContentFields({
  dances,
  karaokes,
  challenges,
  onDancesChange,
  onKaraokesChange,
  onChallengesChange,
  onAddChallenge,
  onAddSong,
  onAddDance,
  canAddTracks,
  datesReady,
}) {
  const values = { dances, karaokes, challenges };
  const setters = {
    dances: onDancesChange,
    karaokes: onKaraokesChange,
    challenges: onChallengesChange,
  };

  return (
    <Box mt={2} mb={1}>
      <label
        style={{
          color: "#828282",
          fontSize: "16px",
          position: "relative",
          top: "-9px",
        }}
      >
        Assigned content (optional)
      </label>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        Link dances, karaoke songs, or challenges to this theme. Their sticker
        uploads can then pull from this theme&apos;s library. New challenges
        and songs created here use this theme&apos;s dates and stickers.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!datesReady}
          onClick={onAddChallenge}
          sx={{ textTransform: "none" }}
        >
          Add challenge
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!datesReady || !canAddTracks}
          onClick={onAddSong}
          sx={{ textTransform: "none" }}
        >
          Add song
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!datesReady || !canAddTracks}
          onClick={onAddDance}
          sx={{ textTransform: "none" }}
        >
          Add dance
        </Button>
      </Box>
      {!datesReady && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Set the theme start and end before adding content.
        </Typography>
      )}
      {datesReady && !canAddTracks && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Save the theme before adding a song or dance. A new challenge is
          linked when you save.
        </Typography>
      )}
      {datesReady && canAddTracks && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Songs and dances open their upload wizard in a new tab and appear
          here when you come back.
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {FIELDS.map((field) => (
          <AssignmentSelect
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            value={values[field.key]}
            onChange={setters[field.key]}
            search={field.search}
            optionLabel={field.optionLabel}
          />
        ))}
      </Box>
    </Box>
  );
}
